import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { Finding, ScanRunView, SiteComponentView, SiteDetail } from '@ametica/shared';
import { useScanSite, useSiteComponents, useSiteDetail, useSiteScans } from '../api/inventory';
import { useSiteFindings } from '../api/findings';
import { SeverityBadge } from '../features/findings/SeverityBadge';
import { FindingHistory, FindingStatusActions } from '../features/findings/FindingStatusActions';
import { CoveragePanel } from '../features/coverage/CoveragePanel';
import { RESOLVED_REASON, SEVERITY, pluralFindings } from '../features/findings/severity';
import { useDeleteSite } from '../api/sites';
import { PageTitle } from '../layout/PageTitle';
import { Icon } from '../components/Icon';
import { useToast } from '../components/Toast';
import { UptimeSoonCard } from '../features/uptime/UptimeSoonCard';
import { formatDateTime, timeAgo } from '../lib/time';

type Tab = 'overview' | 'plugins' | 'themes' | 'vulns' | 'uptime' | 'scans';
const TABS: Array<[Tab, string]> = [
  ['overview', 'Prehľad'], ['plugins', 'Pluginy'], ['themes', 'Témy'], ['vulns', 'Zraniteľnosti'], ['uptime', 'Uptime'], ['scans', 'Skeny'],
];

const CONNECTION: Record<SiteDetail['connection'], { color: string; text: string; label: string }> = {
  ok: { color: 'bg-ok', text: 'text-ok', label: 'Pripojená' },
  stale: { color: 'bg-sev-medium', text: 'text-sev-medium', label: 'Report je zastaraný' },
  error: { color: 'bg-sev-critical', text: 'text-sev-critical', label: 'Nedostupná' },
  never: { color: 'bg-ghost', text: 'text-faint', label: 'Zatiaľ bez reportu' },
};

/** Detail stránky podľa design/SiteDetail.dc.html. Inventár (06), nálezy (08), stavy nálezov a panel pokrytia (11). */
export function SiteDetailPage() {
  const { id = '' } = useParams();
  const site = useSiteDetail(id);
  const scan = useScanSite(id);
  const remove = useDeleteSite();
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('overview');

  if (site.isPending) return <PageTitle title="Stránka" />;
  if (site.isError || !site.data) {
    return (
      <>
        <PageTitle title="Stránka" />
        <div className="card p-6 text-sev-critical">Stránku sa nepodarilo načítať: {site.error?.message}</div>
      </>
    );
  }
  const s = site.data;
  const conn = CONNECTION[s.connection];

  async function runScan() {
    let run: ScanRunView;
    try {
      run = await scan.mutateAsync();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Sken sa nepodarilo spustiť');
      return;
    }
    toast(run.status === 'SUCCEEDED'
      ? `Inventár načítaný — ${run.componentsCount} komponentov, ${run.findingsOpened ?? 0} nových nálezov, ${run.findingsResolved ?? 0} uzavretých`
      : 'Sken zlyhal — pozri stav stránky');
  }

  async function removeSite() {
    if (!window.confirm(`Zmazať stránku ${s.name}? Prestane sa skenovať a zmizne z prehľadov.`)) return;
    await remove.mutateAsync(s.id);
    toast('Stránka zmazaná');
    navigate('/sites', { replace: true });
  }

  return (
    <>
      <PageTitle
        title={s.name}
        sub={`Detail stránky · ${s.host}`}
        actions={<Link to="/sites" className="btn btn-g">← Späť na stránky</Link>}
      />

      <div className="flex flex-wrap items-start justify-between gap-5">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <span className={`dotst h-[9px] w-[9px] ${conn.color}`} />
            <span className="text-[22px] font-semibold tracking-[-.4px]">{s.name}</span>
            <span className={`pill bg-raised ${conn.text}`}>{conn.label}</span>
          </div>
          <a href={s.url} target="_blank" rel="noreferrer" className="mono text-[12.5px]">{s.host}</a>
          <span className="hint">
            {s.lastReportAt ? `posledný report ${timeAgo(s.lastReportAt)} (${formatDateTime(s.lastReportAt)})` : 'žiadny report'}
            {!s.inventoryComplete && <span className="text-sev-high"> · multisite — inventár je neúplný</span>}
          </span>
          {s.connection === 'error' && s.lastReportError && (
            <span className="note border-sev-critical/30 bg-sev-critical/10 text-[12.5px]">
              <Icon name="warn" size={15} className="mt-px shrink-0 text-sev-critical" />
              <span>{s.lastReportError}</span>
            </span>
          )}
        </div>
        <div className="flex gap-2.5">
          <button type="button" className="btn btn-g" onClick={runScan} disabled={scan.isPending}>
            <Icon name="sync" size={15} />
            {scan.isPending ? 'Načítavam…' : 'Znovu načítať inventár'}
          </button>
          <button type="button" className="btn btn-g" onClick={removeSite} disabled={remove.isPending}>Zmazať</button>
        </div>
      </div>

      <div className="grid4">
        <Tile label="WordPress" value={s.wpVersion ?? '—'} foot={coreUpdateFoot(s)} />
        <Tile label="PHP" value={s.phpVersion ?? '—'} foot={s.phpVersion ? 'z posledného reportu' : 'zatiaľ bez reportu'} />
        <Tile label="Pluginy" value={s.counts.plugins} foot={`${s.counts.withUpdate} s aktualizáciou`} />
        <Tile
          label="Otvorené nálezy"
          value={s.connection === 'ok' ? s.findings.open : '?'}
          tone={s.connection === 'ok' && s.findings.worst ? SEVERITY[s.findings.worst].text : undefined}
          foot={
            s.connection === 'error' ? 'nevieme — stránka sa neozvala'
              : s.connection === 'never' ? 'nevieme — bez reportu'
                : s.connection === 'stale' ? 'nevieme — report je zastaraný'
                  : s.findings.worst ? `najhoršia: ${SEVERITY[s.findings.worst].label.toLowerCase()}` : 'žiadny otvorený nález'
          }
        />
      </div>

      <div className="tabs" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={`tab ${tab === k ? 'on' : ''}`} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <CoveragePanel siteId={id} />
          <ComponentsTable siteId={id} filter={(c) => c.type !== 'core'} title="Všetky komponenty" />
        </>
      )}
      {tab === 'plugins' && <ComponentsTable siteId={id} filter={(c) => c.type === 'plugin'} title="Pluginy" />}
      {tab === 'themes' && <ComponentsTable siteId={id} filter={(c) => c.type === 'theme'} title="Témy" />}
      {tab === 'vulns' && <FindingsTable siteId={id} connection={s.connection} />}
      {tab === 'uptime' && <UptimeSoonCard compact />}
      {tab === 'scans' && <ScansTable siteId={id} />}
    </>
  );
}

function coreUpdateFoot(s: SiteDetail): string {
  if (!s.wpVersion) return 'zatiaľ bez reportu';
  return 'verzia z posledného reportu';
}

function Tile({ label, value, foot, tone }: { label: string; value: string | number; foot: string; tone?: string }) {
  return (
    <div className="tile px-[18px] py-4">
      <span className="tile-l">{label}</span>
      <span className={`mono text-lg font-semibold ${tone ?? ''}`}>{value}</span>
      <span className="tile-f">{foot}</span>
    </div>
  );
}

function ComponentsTable({ siteId, filter, title }: { siteId: string; filter: (c: SiteComponentView) => boolean; title: string }) {
  const components = useSiteComponents(siteId);
  const rows = (components.data ?? []).filter(filter);
  const live = rows.filter((c) => !c.removedAt);
  const lastSeen = components.data?.reduce<string | null>((m, c) => (!m || c.lastSeenAt > m ? c.lastSeenAt : m), null) ?? null;

  return (
    <div className="card">
      <div className="tblwrap">
        <table className="tbl">
          <thead>
            <tr><th>{title.toUpperCase()}</th><th>VERZIA</th><th>AKTUALIZÁCIA</th><th>STAV</th><th>ZÁVAŽNOSŤ</th></tr>
          </thead>
          <tbody>
            {components.isPending && <tr><td colSpan={5} className="text-faint">Načítavam…</td></tr>}
            {components.data && rows.length === 0 && <tr><td colSpan={5} className="text-faint">Zatiaľ nič — spusti „Znovu načítať inventár".</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} className={c.removedAt ? 'opacity-50' : ''}>
                <td>
                  <div>{c.name || c.slug}{c.type === 'theme' && <span className="ml-2 text-[11px] text-faint">téma</span>}</div>
                  <div className="sub">{c.slug}{c.file && c.file !== c.slug ? ` · ${c.file}` : ''}</div>
                </td>
                <td className="mono w-[110px]">
                  {c.version || <span className="text-faint" title="Plugin nemá verziu v hlavičke — nedá sa párovať s katalógom">bez verzie</span>}
                </td>
                <td className="w-[130px]">
                  {c.updateAvailable
                    ? <span className="mono inline-flex items-center gap-[5px] text-xs text-dim"><Icon name="up" size={12} className="text-faint" />{c.updateAvailable}</span>
                    : <span className="text-xs text-faint">aktuálny</span>}
                </td>
                <td className="w-[110px]">
                  {c.removedAt
                    ? <span className="pill bg-raised text-faint" title={`odstránený ${formatDateTime(c.removedAt)}`}>odstránený</span>
                    : <span className={`pill ${c.active ? 'bg-ok/15 text-ok' : 'bg-raised text-faint'}`}>{c.active ? 'aktívny' : 'neaktívny'}</span>}
                </td>
                <td className="w-[140px] text-ghost">—</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between px-5 py-3.5 text-[11.5px] text-faint">
        <span>{live.length} {title.toLowerCase()}{rows.length > live.length ? ` · ${rows.length - live.length} odstránených` : ''}</span>
        <span className="mono text-ghost">{lastSeen ? `inventár z ${formatDateTime(lastSeen)}` : ''}</span>
      </div>
    </div>
  );
}

/** Nálezy stránky: otvorené najprv; uzavreté ostávajú s dôvodom, aby bolo vidieť, čo tu bolo a prečo zmizlo. */
function FindingsTable({ siteId, connection }: { siteId: string; connection: SiteDetail['connection'] }) {
  const findings = useSiteFindings(siteId);
  const rows = findings.data ?? [];
  const open = rows.filter((f) => f.status === 'OPEN');
  const closed = rows.length - open.length;

  return (
    <div className="card">
      {connection !== 'ok' && rows.length === 0 && (
        <div className="border-b border-line px-5 py-3 text-[12.5px] text-sev-medium">
          Nula nálezov z tejto stránky nič neznamená — {connection === 'never' ? 'ešte neposlala report' : connection === 'error' ? 'neozvala sa' : 'report je zastaraný'}.
        </div>
      )}
      <div className="tblwrap">
        <table className="tbl">
          <thead>
            <tr><th>ZÁVAŽNOSŤ</th><th>ZRANITEĽNOSŤ</th><th>KOMPONENT</th><th>OPRAVA</th><th>STAV</th><th>OD</th><th>AKCIE</th></tr>
          </thead>
          <tbody>
            {findings.isPending && <tr><td colSpan={7} className="text-faint">Načítavam…</td></tr>}
            {findings.isError && <tr><td colSpan={7} className="text-sev-critical">Nálezy sa nepodarilo načítať: {findings.error.message}</td></tr>}
            {findings.data && rows.length === 0 && <tr><td colSpan={7} className="text-faint">Žiadny nález — ani otvorený, ani uzavretý.</td></tr>}
            {rows.map((f) => <FindingRow key={f.id} f={f} />)}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between px-5 py-3.5 text-[11.5px] text-faint">
        <span>{open.length} {pluralFindings(open.length)} otvorených{closed > 0 ? ` · ${closed} uzavretých` : ''}</span>
        <span className="text-ghost">závažnosť podľa CVSS z katalógu Wordfence · nepotvrdená = bez CVSS</span>
      </div>
    </div>
  );
}

/** Riadok nálezu + (rozbalená) história. Ignorovaný a akceptovaný nález ostáva čitateľný, len stlmený — nezmizol, bol vedome odložený. */
function FindingRow({ f }: { f: Finding }) {
  const [history, setHistory] = useState(false);
  const closed = f.status !== 'OPEN';
  return (
    <>
      <tr className={closed ? 'opacity-60' : ''}>
        <td className="w-[140px]"><SeverityBadge severity={f.severity} /></td>
        <td>
          <div>{f.title}</div>
          <div className="sub">{f.cve ?? 'bez CVE'}{f.cvss !== null ? ` · CVSS ${f.cvss.toFixed(1)}` : ''}</div>
        </td>
        <td className="w-[180px]">
          <div>{f.component.name}{f.component.type !== 'plugin' && <span className="ml-2 text-[11px] text-faint">{f.component.type === 'core' ? 'core' : 'téma'}</span>}</div>
          <div className="sub">{f.component.slug} {f.installedVersion}</div>
        </td>
        <td className="mono w-[120px]">
          {f.patchedIn
            ? <span className="inline-flex items-center gap-[5px] text-xs text-ok"><Icon name="up" size={12} />{f.patchedIn}</span>
            : <span className="text-xs text-faint" title="Katalóg zatiaľ nepozná opravenú verziu vyššiu než nainštalovanú">bez opravy</span>}
        </td>
        <td className="w-[150px]">
          {f.status === 'OPEN' && <span className="pill bg-raised text-dim">otvorený</span>}
          {f.status === 'RESOLVED' && <span className="pill bg-ok/15 text-ok" title={f.resolvedAt ? `uzavretý ${formatDateTime(f.resolvedAt)}` : undefined}>{f.resolvedReason ? RESOLVED_REASON[f.resolvedReason] : 'uzavretý'}</span>}
          {f.status === 'IGNORED' && <span className="pill bg-raised text-faint">ignorovaný</span>}
          {f.status === 'ACCEPTED_RISK' && <span className="pill bg-raised text-faint">akceptované riziko</span>}
        </td>
        <td className="w-[110px] whitespace-nowrap text-[11.5px] text-faint" title={formatDateTime(f.firstDetectedAt)}>{timeAgo(f.firstDetectedAt)}</td>
        <td className="w-[260px]">
          <FindingStatusActions findingId={f.id} status={f.status} historyOpen={history} onToggleHistory={() => setHistory((h) => !h)} />
        </td>
      </tr>
      {history && (
        <tr>
          <td colSpan={7} className="bg-bg/40">
            <FindingHistory findingId={f.id} />
          </td>
        </tr>
      )}
    </>
  );
}

const RUN_STATUS: Record<ScanRunView['status'], { cls: string; label: string }> = {
  RUNNING: { cls: 'bg-raised text-dim', label: 'beží' },
  SUCCEEDED: { cls: 'bg-ok/15 text-ok', label: 'úspešný' },
  FAILED: { cls: 'bg-sev-critical/15 text-sev-critical', label: 'zlyhal' },
};
const TRIGGER: Record<ScanRunView['trigger'], string> = { MANUAL: 'ručne', CRON: 'plánovač', WEBHOOK: 'webhook' };

function ScansTable({ siteId }: { siteId: string }) {
  const scans = useSiteScans(siteId);
  return (
    <div className="card">
      <div className="tblwrap">
        <table className="tbl">
          <thead><tr><th>KEDY</th><th>SPUSTENIE</th><th>STAV</th><th>KOMPONENTY</th><th>NÁLEZY</th><th>CHYBA</th></tr></thead>
          <tbody>
            {scans.data?.length === 0 && <tr><td colSpan={7} className="text-faint">Zatiaľ žiadny sken.</td></tr>}
            {scans.data?.map((r) => (
              <tr key={r.id}>
                <td className="mono whitespace-nowrap">{formatDateTime(r.startedAt)}</td>
                <td>{TRIGGER[r.trigger]}</td>
                <td><span className={`pill ${RUN_STATUS[r.status].cls}`}>{RUN_STATUS[r.status].label}</span></td>
                <td className="mono">{r.componentsCount ?? '—'}</td>
                <td className="mono text-xs text-dim">{r.status === 'SUCCEEDED' ? `+${r.findingsOpened ?? 0} / −${r.findingsResolved ?? 0}` : '—'}</td>
                <td className="max-w-[420px] truncate text-xs text-dim" title={r.error ?? undefined}>{r.error ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
